import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { api } from '../../api'
import { dayThangLenR2 } from '../../lib/uploadR2'
import { useModal } from '../../components/Modal'
import {
  buildIframeDocument,
  buildMergedData,
  resolveTemplateUrl,
  resolveWriteTarget,
} from '../../lib/templateEngine'
import {
  IconChevronLeft,
  IconHeart,
  IconPalette,
  IconCheck,
  IconLock,
  IconExternalLink,
  IconSparkles,
  IconAlertCircle,
  IconQrCode,
  IconMusic,
  IconHelpCircle,
  IconEdit3,
  IconSliders,
  IconSend,
} from '../../components/Icons'
import BankQrPanel from '../../components/BankQrPanel'
import MusicPanel from '../../components/MusicPanel'
import DisplaySettingsPanel from '../../components/DisplaySettingsPanel'
import ChangeTemplateModal from '../../components/ChangeTemplateModal'
import ChangeSlugModal from '../../components/ChangeSlugModal'
import ImageCropper from '../../components/ImageCropper'
import ImagePickerModal from '../../components/ImagePickerModal'
import EditorTourGuide from '../../components/EditorTourGuide'
import './editor-responsive.css'

// data:image/png;base64,... -> File (để upload QR sinh ra làm ảnh thiệp)
function dataUriToFile(uri, name) {
  try {
    const [head, b64] = uri.split(',')
    const mime = (head.match(/:(.*?);/) || [])[1] || 'image/png'
    const bin = atob(b64)
    const arr = new Uint8Array(bin.length)
    for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i)
    return new File([arr], name, { type: mime })
  } catch (_e) {
    return null
  }
}

export default function EditorPage() {
  const { invitationId } = useParams()
  const navigate = useNavigate()
  const modal = useModal()
  const iframeRef = useRef(null)
  const templateHtmlRef = useRef('') // HTML gốc của mẫu, để dựng lại iframe khi cần
  const scrollRestoreRef = useRef(0)

  const [invitation, setInvitation] = useState(null)
  const [iframeDoc, setIframeDoc] = useState('')
  const [iframeKey, setIframeKey] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [saveStatus, setSaveStatus] = useState('idle') // idle | saving | saved | error
  const [pending, setPending] = useState({ inv: {}, groom: {}, bride: {}, extra: {} })
  const [lockState, setLockState] = useState(null)
  const [bankPanel, setBankPanel] = useState(false)
  const [musicPanel, setMusicPanel] = useState(false)
  const [displaySettingsPanel, setDisplaySettingsPanel] = useState(false)
  const [changeTemplateModalOpen, setChangeTemplateModalOpen] = useState(false)
  const [changeSlugModalOpen, setChangeSlugModalOpen] = useState(false)
  const [isEditingTitle, setIsEditingTitle] = useState(false)
  const [titleDraft, setTitleDraft] = useState('')
  const [savingTitle, setSavingTitle] = useState(false)

  const handleSaveTitle = async () => {
    const trimmed = titleDraft.trim()
    if (!trimmed || !invitation?.id) {
      setIsEditingTitle(false)
      return
    }
    setSavingTitle(true)
    try {
      await api.setInvitationTitle(invitation.id, trimmed)
      setInvitation((prev) => ({ ...prev, title_vi: trimmed, title: trimmed }))
      setIsEditingTitle(false)
    } catch (err) {
      alert(err?.response?.data?.message || 'Lỗi khi lưu tiêu đề')
    } finally {
      setSavingTitle(false)
    }
  }
  const [showTour, setShowTour] = useState(() => {
    try {
      return !localStorage.getItem('has_seen_editor_tour_v1')
    } catch {
      return false
    }
  })
  const [imagePicker, setImagePicker] = useState(null) // { field, index, ratio }
  const [cropTask, setCropTask] = useState(null) // { file, ratio, field, index }
  // Hàng đợi cắt ảnh khi tải NHIỀU ảnh album 1 lần: cắt xong ảnh này -> lưu -> ảnh kế.
  const [cropQueue, setCropQueue] = useState(null) // { files: File[], ratio, field, baseIndex, pos, total }
  const galleryUploadRef = useRef(null)

  const GALLERY_RATIO = 0.72 // ~3:4, khớp khung carousel album của mẫu

  const nextGalleryIndex = useCallback(
    () =>
      (invitation?.images || [])
        .filter((img) => img.image_type === 'gallery')
        .reduce((max, img) => Math.max(max, (img.sort_order ?? 0) + 1), 0),
    [invitation],
  )

  const startCropQueue = useCallback(
    (files, ratio, field = 'gallery') => {
      const list = Array.from(files || []).filter((f) => f && f.type?.startsWith('image/'))
      if (!list.length) return
      const baseIndex = field === 'gallery' ? nextGalleryIndex() : 0
      setCropQueue({ files: list, ratio: ratio || GALLERY_RATIO, field, baseIndex, pos: 0, total: list.length })
      setCropTask({ file: list[0], ratio: ratio || GALLERY_RATIO, field, index: baseIndex, queued: true })
    },
    [nextGalleryIndex],
  )

  const dirty = useMemo(
    () =>
      Object.keys(pending.inv).length +
        Object.keys(pending.groom).length +
        Object.keys(pending.bride).length +
        Object.keys(pending.extra).length >
      0,
    [pending],
  )

  const locked = Boolean(lockState?.locked)
  const editsLeft = lockState?.editsLeft ?? null

  const mergedForIframe = useCallback(
    (inv, pend) => ({
      ...inv,
      ...pend.inv,
      extra_data: { ...(inv?.extra_data || {}), ...pend.extra },
      groom: { ...(inv?.groom || {}), ...pend.groom },
      bride: { ...(inv?.bride || {}), ...pend.bride },
    }),
    [],
  )

  useEffect(() => {
    let cancelled = false
    const run = async () => {
      try {
        setLoading(true)
        setError('')
        const res = await api.getInvitationById(invitationId)
        const invData = res.data?.data || res.data
        if (!invData) throw new Error('Không tìm thấy thiệp')

        const isLocked = Boolean(invData.lock_state?.locked)
        const editEntryPath = invData.template?.schema?.editHtmlPath ||
          (invData.template?.html_path && invData.template.html_path.includes('vobe2.html')
            ? invData.template.html_path.replace('vobe2.html', 'phongbibe2.html')
            : null)
        const templateUrl = resolveTemplateUrl(editEntryPath || invData.template?.html_path)
        if (!templateUrl) throw new Error('Thiệp này chưa được gán mẫu giao diện')

        const bustUrl = `${templateUrl}${templateUrl.includes('?') ? '&' : '?'}v=${Date.now()}`
        const htmlRes = await fetch(bustUrl, { cache: 'no-store' })
        if (!htmlRes.ok) throw new Error(`Không tải được mẫu: ${templateUrl}`)
        const html = await htmlRes.text()

        if (cancelled) return
        templateHtmlRef.current = html
        setInvitation(invData)
        setLockState(invData.lock_state || null)
        // Quyền sửa do admin cấu hình trên MẪU: ô nào không được chọn thì vẫn
        // hiện dữ liệu nhưng không bấm sửa tại chỗ được.
        setIframeDoc(buildIframeDocument(html, invData, {
          editMode: !isLocked,
          editableFields: invData?.template?.editable_fields ?? null,
          imageSlotRules: invData?.template?.image_slot_rules ?? null,
        }))
      } catch (err) {
        if (!cancelled) setError(err?.response?.data?.message || err.message || 'Có lỗi xảy ra')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    run()
    return () => {
      cancelled = true
    }
  }, [invitationId])

  const pushToIframe = useCallback(
    (inv, pend) => {
      const merged = buildMergedData(mergedForIframe(inv, pend))
      iframeRef.current?.contentWindow?.postMessage({ type: 'wedding-web:data-updated', data: merged }, '*')
    },
    [mergedForIframe],
  )

  // Album ảnh: chèn/xoá ô carousel LÚC ĐANG CHẠY khiến engine gallery của LadiPage
  // desync -> gallery trắng. Cách chắc ăn là dựng lại iframe để LadiPage init lại
  // sạch với đúng số ảnh; giữ nguyên vị trí cuộn cho đỡ giật.
  const rebuildIframe = useCallback(
    (inv, pend) => {
      if (!templateHtmlRef.current) return
      scrollRestoreRef.current = iframeRef.current?.contentWindow?.scrollY || 0
      setIframeDoc(
        buildIframeDocument(templateHtmlRef.current, mergedForIframe(inv, pend), {
          editMode: !locked,
          editableFields: inv?.template?.editable_fields ?? null,
          imageSlotRules: inv?.template?.image_slot_rules ?? null,
        }),
      )
      setIframeKey((k) => k + 1)
    },
    [mergedForIframe, locked],
  )

  const handleIframeLoad = useCallback(() => {
    const y = scrollRestoreRef.current
    if (!y) return
    let tries = 0
    const tick = () => {
      const win = iframeRef.current?.contentWindow
      if (win) win.scrollTo(0, y)
      if (++tries < 20) setTimeout(tick, 80)
    }
    tick()
  }, [])

  const stageField = useCallback(
    (field, value) => {
      setPending((prev) => {
        const target = resolveWriteTarget(field)
        const next = { inv: { ...prev.inv }, groom: { ...prev.groom }, bride: { ...prev.bride }, extra: { ...prev.extra } }
        if (target.scope === 'groom') next.groom[target.column] = value
        else if (target.scope === 'bride') next.bride[target.column] = value
        else if (target.scope === 'extra_data') next.extra[target.column] = value
        else next.inv[target.column] = value
        pushToIframe(invitation, next)
        return next
      })
    },
    [invitation, pushToIframe],
  )

  const saveImage = useCallback(
    async (field, index, file) => {
      if (locked) {
        await modal.alert({ tone: 'warn', title: 'Thiệp đã khoá', message: lockNote(lockState) })
        return
      }
      setSaveStatus('saving')
      try {
        const existing = (invitation.images || []).find(
          (img) => img.image_type === field && (index === null ? true : (img.sort_order || 0) === index),
        )
        // Ưu tiên đẩy thẳng lên R2: ảnh không đi qua đường mạng của máy chủ nhà.
        // Chưa bật R2 (hoặc đẩy thẳng hỏng) thì trả null -> rơi xuống cách cũ.
        let saved = await dayThangLenR2(file, {
          invitationId: invitation.id,
          imageType: field,
          sortOrder: index ?? 0,
          imageAlt: field,
          replaceId: existing ? existing.id : null,
        })

        if (!saved) {
          const formData = new FormData()
          formData.append('image', file)
          formData.append('invitation_id', invitation.id)
          formData.append('image_type', field)
          formData.append('sort_order', String(index ?? 0))
          formData.append('image_alt', field)

          if (existing) {
            const res = await api.updateInvitationImage(existing.id, formData)
            saved = res.data?.data || res.data
          } else {
            const res = await api.createInvitationImage(formData)
            saved = res.data?.data || res.data
          }
        }
        const nextImages = existing
          ? (invitation.images || []).map((img) => (img.id === existing.id ? saved : img))
          : [...(invitation.images || []), saved]
        const nextInvitation = { ...invitation, images: nextImages }
        setInvitation(nextInvitation)
        pushToIframe(nextInvitation, pending)
        setSaveStatus('saved')
        return nextInvitation
      } catch (err) {
        console.error('Save image failed:', err)
        setSaveStatus('error')
        return null
      }
    },
    [invitation, pending, locked, lockState, modal, pushToIframe],
  )

  const queueLatestInvRef = useRef(null)

  // Cắt xong 1 ảnh trong hàng đợi album -> lưu -> chuyển sang ảnh kế (hoặc kết thúc).
  const advanceQueue = useCallback(
    async (croppedFileOrNull) => {
      const q = cropQueue
      if (!q) { setCropTask(null); return }
      setCropTask(null)
      if (croppedFileOrNull) {
        const previewUrl = URL.createObjectURL(croppedFileOrNull)
        iframeRef.current?.contentWindow?.postMessage({
          type: 'wedding-web:optimistic-image',
          field: q.field,
          index: q.baseIndex + q.pos,
          url: previewUrl,
        }, '*')
        const inv = await saveImage(q.field, q.baseIndex + q.pos, croppedFileOrNull)
        if (inv) queueLatestInvRef.current = inv
      }
      const nextPos = q.pos + 1
      if (nextPos < q.total) {
        setCropQueue({ ...q, pos: nextPos })
        setCropTask({ file: q.files[nextPos], ratio: q.ratio, field: q.field, index: q.baseIndex + nextPos, queued: true })
      } else {
        setCropQueue(null)
        if (q.field === 'gallery') rebuildIframe(queueLatestInvRef.current || invitation, pending)
        queueLatestInvRef.current = null
        await modal.alert({ tone: 'success', title: 'Đã thêm ảnh', message: `Đã thêm ${q.total} ảnh vào album.` })
      }
    },
    [cropQueue, saveImage, modal, rebuildIframe, invitation, pending],
  )

  useEffect(() => {
    const handleMessage = (event) => {
      if (!event.data) return
      if (event.data.type === 'wedding-web:open-bank-qr') {
        if (locked) modal.alert({ tone: 'warn', title: 'Thiệp đã khoá', message: lockNote(lockState) })
        else setBankPanel(true)
        return
      }
      if (event.data.type === 'wedding-web:choose-image') {
        if (locked) {
          modal.alert({ tone: 'warn', title: 'Thiệp đã khoá', message: lockNote(lockState) })
          return
        }
        const { field, index, ratio } = event.data
        setImagePicker({ field, index, ratio })
        return
      }
      if (event.data.type === 'wedding-web:edit') {
        const { field, kind, value, index, file, files } = event.data
        if (kind === 'image' && file) {
          if (locked) { modal.alert({ tone: 'warn', title: 'Thiệp đã khoá', message: lockNote(lockState) }); return }
          // Mở popup CẮT ẢNH trước khi upload (không gửi ảnh gốc).
          setCropTask({ file, ratio: event.data.ratio || null, field, index })
        } else if (kind === 'image-batch' && files?.length) {
          if (locked) { modal.alert({ tone: 'warn', title: 'Thiệp đã khoá', message: lockNote(lockState) }); return }
          startCropQueue(files, event.data.ratio, field)
        } else if (kind === 'field') stageField(field, value)
        return
      }
      if (event.data.type === 'wedding-web:navigate' && event.data.file && invitation) {
        const baseUrl = resolveTemplateUrl(invitation.template?.html_path)
        if (!baseUrl) return
        try {
          const fullBase = /^https?:\/\//i.test(baseUrl)
            ? baseUrl
            : (typeof window !== 'undefined' ? new URL(baseUrl, window.location.origin).href : baseUrl)
          const nextUrl = new URL(event.data.file, fullBase).href
          const bustNextUrl = `${nextUrl}${nextUrl.includes('?') ? '&' : '?'}v=${Date.now()}`
          fetch(bustNextUrl, { cache: 'no-store' })
            .then((r) => r.text())
            .then((html) => {
              templateHtmlRef.current = html
              setIframeDoc(buildIframeDocument(html, mergedForIframe(invitation, pending), {
                editMode: !locked,
                editableFields: invitation?.template?.editable_fields ?? null,
                imageSlotRules: invitation?.template?.image_slot_rules ?? null,
              }))
              setIframeKey((k) => k + 1)
            })
            .catch((err) => console.error('Editor navigate failed:', err))
        } catch (err) {
          console.error('Editor navigate failed:', err)
        }
      }
    }
    window.addEventListener('message', handleMessage)
    return () => window.removeEventListener('message', handleMessage)
  }, [stageField, saveImage, startCropQueue, invitation, pending, locked, lockState, modal, mergedForIframe])

  const applyServerResult = (data) => {
    setInvitation((prev) => ({ ...prev, ...data, groom: data.groom || prev.groom, bride: data.bride || prev.bride }))
    setLockState(data.lock_state || null)
    setPending({ inv: {}, groom: {}, bride: {}, extra: {} })
  }

  const handleSave = async () => {
    if (!dirty || locked) return
    const left = editsLeft ?? '?'
    const ok = await modal.confirm({
      tone: 'default',
      title: 'Xác nhận lưu thiệp cưới',
      message: `Bạn còn ${left} lượt sửa.\nLưu lần này sẽ trừ 1 lượt (còn ${Math.max((editsLeft ?? 1) - 1, 0)} lượt).\n\nBạn có chắc chắn muốn lưu các thay đổi này?`,
      confirmText: 'Lưu Ngay',
      cancelText: 'Chưa Lưu',
    })
    if (!ok) return

    setSaveStatus('saving')
    try {
      const invPayload = { ...pending.inv }
      if (Object.keys(pending.extra).length) {
        invPayload.extra_data = { ...(invitation.extra_data || {}), ...pending.extra }
      }
      const res = await api.editorSaveInvitation(invitation.id, {
        invitation: invPayload,
        groom: pending.groom,
        bride: pending.bride,
      })
      const data = res.data?.data || res.data
      applyServerResult(data)
      setSaveStatus('saved')
      const st = data.lock_state
      if (st?.locked) {
        await modal.alert({
          tone: 'warn',
          title: 'Đã lưu — Thiệp khoá sửa',
          message: `Đã lưu thành công.\n${lockNote(st)}`,
        })
      } else {
        await modal.alert({
          tone: 'success',
          title: 'Đã lưu thành công',
          message: `Đã lưu thông tin thiệp cưới. Bạn còn ${st?.editsLeft ?? '?'} lượt sửa.`,
        })
      }
    } catch (err) {
      setSaveStatus('error')
      const msg = err?.response?.data?.message || err.message || 'Lưu thất bại'
      const st = err?.response?.data?.lockState
      if (st) setLockState(st)
      await modal.alert({ tone: 'danger', title: 'Không lưu được', message: msg })
    }
  }

  const handleChangeTemplate = async () => {
    if (locked) {
      await modal.alert({ tone: 'warn', title: 'Thiệp đã khoá', message: lockNote(lockState) })
      return
    }
    setChangeTemplateModalOpen(true)
  }

  const openPanel = (setter) => async () => {
    if (locked) { await modal.alert({ tone: 'warn', title: 'Thiệp đã khoá', message: lockNote(lockState) }); return }
    setter(true)
  }
  const openBankPanel = openPanel(setBankPanel)
  // Mẫu thiệp có hộp nhạc không. Cột has_music_box do admin chốt; chưa chốt thì
  // suy từ manifest (theme có thẻ <audio> hay không). Mẫu không có hộp nhạc mà
  // vẫn hiện nút chọn nhạc thì khách chọn xong không nghe thấy gì, tưởng hỏng.
  const coHopNhac = (() => {
    const tpl = invitation?.template
    if (!tpl) return true
    if (tpl.has_music_box != null) return Boolean(tpl.has_music_box)
    return Boolean(tpl.manifest && tpl.manifest.has_music_box)
  })()

  const openMusicPanel = openPanel(setMusicPanel)
  const openDisplaySettingsPanel = openPanel(setDisplaySettingsPanel)

  const displaySettingsInvitation = (settings) => {
    const nextExtra = {
      ...(invitation?.extra_data || {}),
      show_rsvp: settings.show_rsvp,
      allow_wishes: settings.allow_wishes,
      show_wishes_list: settings.show_wishes_list,
      show_gift_box: settings.show_gift_box,
      background_color: settings.background_color,
      decor_color: settings.decor_color,
      text_color: settings.text_color,
      cover_overlay_color: settings.cover_overlay_color,
    }
    return {
      ...invitation,
      extra_data: nextExtra,
      show_rsvp: settings.show_rsvp,
      allow_wishes: settings.allow_wishes,
      show_wishes_list: settings.show_wishes_list,
      show_gift_box: settings.show_gift_box,
      background_color: settings.background_color,
      decor_color: settings.decor_color,
      text_color: settings.text_color,
      cover_overlay_color: settings.cover_overlay_color,
    }
  }

  const handleDisplaySettingsPreview = (settings) => {
    pushToIframe(displaySettingsInvitation(settings), pending)
  }

  const closeDisplaySettingsPanel = () => {
    setDisplaySettingsPanel(false)
    pushToIframe(invitation, pending)
  }

  const handleDisplaySettingsSaved = (settings) => {
    setDisplaySettingsPanel(false)
    const nextInv = displaySettingsInvitation(settings)
    setInvitation(nextInv)
    pushToIframe(nextInv, pending)
    modal.alert({
      tone: 'success',
      title: 'Đã lưu cài đặt giao diện',
      message: 'Các tuỳ biến tính năng và ảnh nền đã được áp dụng thành công vào thiệp cưới.',
    })
  }


  // Sau khi quét + lưu QR mừng cưới (hỗ trợ cả cô dâu & chú rể + chọn chế độ hiển thị):
  // Cập nhật extra_data.bank + đẩy vào iframe để hiển thị ngay lập tức.
  const handleBankSaved = async (info) => {
    setBankPanel(false)
    const nextInv = {
      ...invitation,
      extra_data: { ...(invitation.extra_data || {}), bank: info.bank },
      bank: info.bank,
    }
    if (info.bank?.groom && nextInv.groom) {
      nextInv.groom = { ...nextInv.groom, ...info.bank.groom }
    }
    if (info.bank?.bride && nextInv.bride) {
      nextInv.bride = { ...nextInv.bride, ...info.bank.bride }
    }

    setInvitation(nextInv)
    pushToIframe(nextInv, pending)

    // Đồng bộ ảnh QR nếu có dạng data URI
    try {
      if (info.groomQr && typeof info.groomQr === 'string' && info.groomQr.startsWith('data:image')) {
        const fileGroom = dataUriToFile(info.groomQr, 'bank-qr-groom.png')
        if (fileGroom) await saveImage('bank_qr', 0, fileGroom)
      }
      if (info.brideQr && typeof info.brideQr === 'string' && info.brideQr.startsWith('data:image')) {
        const fileBride = dataUriToFile(info.brideQr, 'bank-qr-bride.png')
        if (fileBride) await saveImage('bank_qr', 1, fileBride)
      }
    } catch (_err) {}

    await modal.alert({ tone: 'success', title: 'Đã lưu QR mừng cưới', message: 'Mã QR & tài khoản ngân hàng đã được lưu và cập nhật vào thiệp.' })
  }

  // Sau khi lưu nhạc: cập nhật music_url + playlist + music_settings local + đẩy vào iframe.
  const handleMusicSaved = (data) => {
    setMusicPanel(false)
    const nextInv = {
      ...invitation,
      ...(data || {}),
      music_url: data?.music_url !== undefined ? data.music_url : (invitation?.music_url || ''),
      extra_data: {
        ...(invitation?.extra_data || {}),
        ...(data?.extra_data || {}),
        music_playlist: data?.extra_data?.music_playlist !== undefined
          ? data.extra_data.music_playlist
          : (data?.music_playlist || invitation?.extra_data?.music_playlist || []),
        music_settings: data?.extra_data?.music_settings || invitation?.extra_data?.music_settings,
      },
    }
    setInvitation(nextInv)
    pushToIframe(nextInv, pending)
  }

  if (loading) {
    return (
      <div style={S.center}>
        <div style={S.loadCard}>
          <IconSparkles size={32} color="#d97757" />
          <p style={{ marginTop: 12, fontSize: 14, color: '#5c524c', fontWeight: 500 }}>
            Đang tải trình chỉnh sửa thiệp cưới...
          </p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div style={S.center}>
        <div style={{ ...S.loadCard, borderColor: 'rgba(192,73,56,0.3)' }}>
          <p style={{ color: '#c04938', marginBottom: 14, fontSize: 14, fontWeight: 600 }}>❌ {error}</p>
          <button style={S.btnBackHome} onClick={() => navigate('/dashboard')}>
            Quay lại Danh Sách Thiệp
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="editor-page" style={S.wrap}>
      <header className="editor-topbar" style={S.topbar}>
        <div className="editor-left" style={S.left}>
          <button className="editor-back" style={S.backBtn} onClick={() => navigate('/dashboard')}>
            <IconChevronLeft size={16} />
            <span>Danh Sách Thiệp</span>
          </button>

          <div className="editor-title-wrap" style={S.titleWrap}>
            <IconHeart size={16} color="#d97757" />
            {isEditingTitle ? (
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <input
                  autoFocus
                  style={{
                    fontSize: 13.5,
                    fontWeight: 600,
                    padding: '2px 8px',
                    borderRadius: 4,
                    border: '1px solid #d97757',
                    outline: 'none',
                    height: 26,
                    background: '#fff',
                    color: '#2c2523',
                  }}
                  value={titleDraft}
                  onChange={(e) => setTitleDraft(e.target.value)}
                  onKeyDown={async (e) => {
                    if (e.key === 'Enter') await handleSaveTitle()
                    if (e.key === 'Escape') setIsEditingTitle(false)
                  }}
                  disabled={savingTitle}
                />
                <button
                  type="button"
                  style={{
                    padding: '2px 8px',
                    height: 26,
                    fontSize: 12,
                    background: '#d97757',
                    color: '#fff',
                    border: 'none',
                    borderRadius: 4,
                    cursor: 'pointer',
                    fontWeight: 600,
                  }}
                  onClick={handleSaveTitle}
                  disabled={savingTitle}
                >
                  {savingTitle ? '...' : 'Lưu'}
                </button>
                <button
                  type="button"
                  style={{
                    padding: '2px 6px',
                    height: 26,
                    fontSize: 12,
                    background: '#eee',
                    border: 'none',
                    borderRadius: 4,
                    cursor: 'pointer',
                  }}
                  onClick={() => setIsEditingTitle(false)}
                >
                  ✕
                </button>
              </div>
            ) : (
              <span
                className="editor-title"
                style={{ ...S.title, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 }}
                onClick={() => {
                  setTitleDraft(invitation?.title_vi || invitation?.title || '')
                  setIsEditingTitle(true)
                }}
                title="Bấm để đổi tiêu đề thiệp"
              >
                {invitation?.title_vi || 'Thiệp cưới của bạn'}
                <IconEdit3 size={13} style={{ opacity: 0.6 }} />
              </span>
            )}
          </div>
        </div>

        <div className="editor-center-status" style={S.centerStatus}>
          {locked ? (
            <span style={S.lockBadge}>
              <IconLock size={14} />
              <span>Đã khoá sửa</span>
            </span>
          ) : (
            <span
              style={{
                ...S.statusPill,
                color: saveStatus === 'error' ? '#c04938' : saveStatus === 'saved' ? '#4f7e65' : '#6e625c',
                background:
                  saveStatus === 'error'
                    ? 'rgba(192,73,56,0.1)'
                    : saveStatus === 'saved'
                    ? 'rgba(79,126,101,0.12)'
                    : '#f6f1eb',
              }}
            >
              {saveStatus === 'saving' && '⏳ Đang lưu…'}
              {saveStatus === 'saved' && '✓ Đã lưu thay đổi'}
              {saveStatus === 'error' && '⚠ Lưu thất bại'}
              {saveStatus === 'idle' && (dirty ? '● Có thay đổi chưa lưu' : '💡 Chạm chữ hoặc ảnh trên thiệp để sửa')}
            </span>
          )}
        </div>

        <div className="editor-actions" style={S.rightActions}>
          <button
            className="editor-action"
            style={{ ...S.tplBtn, background: '#fdf6ee', borderColor: '#f2ded0', color: '#c96547', fontWeight: 600 }}
            onClick={openDisplaySettingsPanel}
            disabled={locked}
            title="Đổi màu nền thiệp (RGB/HEX) và bật tắt các khối chức năng"
          >
            <IconPalette size={15} color="#c96547" />
            <span>Màu Nền & Tính Năng</span>
          </button>

          <button className="editor-action" style={S.tplBtn} onClick={openMusicPanel} disabled={locked} title="Cài đặt nhạc nền và ghi âm lời chúc">
            <IconMusic size={15} />
            <span>Âm Thanh & Nhạc</span>
          </button>

          <button
            className="editor-action"
            style={{ ...S.tplBtn, background: '#fdf6ee', borderColor: '#f2ded0', color: '#c96547', fontWeight: 700 }}
            onClick={() => setShowTour(true)}
            title="Xem hướng dẫn sử dụng cách sửa thiệp"
          >
            <IconHelpCircle size={15} color="#c96547" />
            <span>Hướng Dẫn</span>
          </button>

          <button className="editor-action" style={S.tplBtn} onClick={openBankPanel} disabled={locked}>
            <IconQrCode size={15} />
            <span>QR Ngân Hàng</span>
          </button>

          <button
            className="editor-action"
            style={S.tplBtn}
            onClick={() => galleryUploadRef.current?.click()}
            disabled={locked}
            title="Chọn nhiều ảnh cùng lúc để thêm vào album"
          >
            <IconSparkles size={15} />
            <span>Thêm Ảnh Album</span>
          </button>

          <button className="editor-action" style={S.tplBtn} onClick={handleChangeTemplate} disabled={locked}>
            <IconPalette size={15} />
            <span>Đổi Mẫu</span>
          </button>

          <button
            className="editor-action"
            style={S.tplBtn}
            onClick={() => setChangeSlugModalOpen(true)}
            title="Tuỳ chỉnh link thiệp cưới (slug) không lo trùng lặp"
          >
            <span style={{ fontSize: 13 }}>🔗</span>
            <span>Đổi Link</span>
          </button>

          <button
            className="editor-action"
            style={{ ...S.tplBtn, background: '#fdf6ee', borderColor: '#f2ded0', color: '#c96547' }}
            onClick={() => navigate(`/dashboard/invite?invitation=${encodeURIComponent(invitation?.id || invitationId)}`)}
            title="Tạo link riêng và chọn ảnh hiển thị khi gửi link"
          >
            <IconSend size={15} />
            <span>Link &amp; Ảnh Nhúng</span>
          </button>

          <button
            className="editor-action editor-save"
            style={{ ...S.saveBtn, opacity: !dirty || locked || saveStatus === 'saving' ? 0.5 : 1 }}
            onClick={handleSave}
            disabled={!dirty || locked || saveStatus === 'saving'}
          >
            <IconCheck size={16} />
            <span>{locked ? 'Đã Khoá' : `Lưu Thiệp${editsLeft != null ? ` (còn ${editsLeft})` : ''}`}</span>
          </button>

          {invitation?.invitation_slug && (
            <a className="editor-view-link" style={S.viewLink} href={`/${invitation.invitation_slug}`} target="_blank" rel="noreferrer">
              <IconExternalLink size={15} />
              <span>Xem Thiệp</span>
            </a>
          )}
        </div>
      </header>

      {locked && (
        <div className="editor-lockbar" style={S.lockBar}>
          <IconLock size={15} />
          <span>{lockNote(lockState)} — Bạn và khách vẫn có thể xem và chia sẻ thiệp bình thường.</span>
        </div>
      )}

      <iframe
        key={iframeKey}
        ref={iframeRef}
        srcDoc={iframeDoc}
        onLoad={handleIframeLoad}
        className="editor-frame"
        style={S.iframe}
        sandbox="allow-scripts allow-same-origin allow-popups allow-modals allow-forms allow-downloads"
        title="Trình sửa thiệp cưới"
      />

      {bankPanel && (
        <BankQrPanel invitation={invitation} onSaved={handleBankSaved} onClose={() => setBankPanel(false)} />
      )}
      {musicPanel && (
        <MusicPanel invitation={invitation} onSaved={handleMusicSaved} onClose={() => setMusicPanel(false)} />
      )}
      {displaySettingsPanel && (
        <DisplaySettingsPanel
          invitation={invitation}
          onSaved={handleDisplaySettingsSaved}
          onPreview={handleDisplaySettingsPreview}
          onClose={closeDisplaySettingsPanel}
        />
      )}
      {changeTemplateModalOpen && (
        <ChangeTemplateModal
          isOpen={changeTemplateModalOpen}
          onClose={() => setChangeTemplateModalOpen(false)}
          currentTemplateId={invitation?.template?.id || invitation?.template_id}
          invitationId={invitation?.id}
          onSuccess={() => {
            window.location.reload()
          }}
        />
      )}
      {changeSlugModalOpen && (
        <ChangeSlugModal
          isOpen={changeSlugModalOpen}
          onClose={() => setChangeSlugModalOpen(false)}
          invitation={invitation}
          onSlugChanged={(newSlug) => {
            setInvitation((prev) => ({ ...prev, invitation_slug: newSlug, slug: newSlug }))
          }}
        />
      )}
      <EditorTourGuide
        isOpen={showTour}
        onClose={() => setShowTour(false)}
        onComplete={() => {
          try {
            localStorage.setItem('has_seen_editor_tour_v1', 'true')
          } catch {}
          setShowTour(false)
        }}
      />
      {imagePicker && (
        <ImagePickerModal
          slot={imagePicker.field}
          index={imagePicker.index}
          ratio={imagePicker.ratio}
          existingImages={invitation?.images || []}
          onUploadNew={(file) => {
            const p = imagePicker
            setImagePicker(null)
            if (file && p) {
              setCropTask({ file, ratio: p.ratio || null, field: p.field, index: p.index })
            }
          }}
          onSelectExisting={async (sourceImage) => {
            const p = imagePicker
            setImagePicker(null)
            if (!p || !sourceImage) return

            // 1. Cập nhật giao diện iframe ngay lập tức (<50ms)
            iframeRef.current?.contentWindow?.postMessage({
              type: 'wedding-web:optimistic-image',
              field: p.field,
              index: p.index,
              url: sourceImage.image_url,
            }, '*')

            // 2. Gán trong state React
            const targetField = p.field
            const targetIndex = p.index ?? 0
            const existingSlot = (invitation?.images || []).find(
              (img) => img.image_type === targetField && (p.index === null ? true : (img.sort_order || 0) === targetIndex)
            )

            const optAssigned = {
              ...(existingSlot || {}),
              id: existingSlot ? existingSlot.id : 'opt-' + Date.now(),
              invitation_id: invitation.id,
              image_url: sourceImage.image_url,
              image_type: targetField,
              sort_order: targetIndex,
              variants: sourceImage.variants || null,
              storage_driver: sourceImage.storage_driver,
              storage_key: sourceImage.storage_key,
              width: sourceImage.width,
              height: sourceImage.height,
            }

            const nextImgs = existingSlot
              ? (invitation.images || []).map((img) => (img.id === existingSlot.id ? optAssigned : img))
              : [...(invitation.images || []), optAssigned]
            const nextInv = { ...invitation, images: nextImgs }
            setInvitation(nextInv)
            pushToIframe(nextInv, pending)

            // 3. Gọi API assign không tốn thời gian upload
            setSaveStatus('saving')
            try {
              const res = await api.assignInvitationImage({
                invitation_id: invitation.id,
                source_image_id: sourceImage.id,
                image_type: targetField,
                sort_order: targetIndex,
              })
              const saved = res.data?.data || res.data
              const finalImgs = (nextInv.images || []).map((img) =>
                (img.id === optAssigned.id || (img.image_type === targetField && (img.sort_order || 0) === targetIndex))
                  ? saved
                  : img
              )
              const finalInv = { ...nextInv, images: finalImgs }
              setInvitation(finalInv)
              pushToIframe(finalInv, pending)
              setSaveStatus('saved')
            } catch (err) {
              console.error('Assign existing image failed:', err)
              setSaveStatus('error')
              modal.alert({ tone: 'danger', title: 'Lỗi gán ảnh', message: err?.response?.data?.message || err.message })
            }
          }}
          onClose={() => setImagePicker(null)}
        />
      )}
      {cropTask && (
        <ImageCropper
          key={cropTask.file?.name + '|' + cropTask.index}
          file={cropTask.file}
          ratio={cropTask.ratio}
          queueLabel={cropQueue ? `Ảnh ${cropQueue.pos + 1}/${cropQueue.total}` : null}
          onCancel={() => (cropQueue ? advanceQueue(null) : setCropTask(null))}
          onDone={async (croppedFile) => {
            const t = cropTask
            if (cropQueue) { advanceQueue(croppedFile); return }
            setCropTask(null)

            // OPTIMISTIC PREVIEW: Cập nhật ngay lập tức vào thiệp (<50ms)
            const previewUrl = URL.createObjectURL(croppedFile)
            iframeRef.current?.contentWindow?.postMessage({
              type: 'wedding-web:optimistic-image',
              field: t.field,
              index: t.index,
              url: previewUrl,
            }, '*')

            await saveImage(t.field, t.index, croppedFile)
          }}
        />
      )}
      {galleryUploadRef && (
        <input
          ref={galleryUploadRef}
          type="file"
          accept="image/*"
          multiple
          style={{ display: 'none' }}
          onChange={(e) => {
            // Sao chép File ra mảng TRƯỚC khi reset input (reset sẽ xoá luôn e.target.files).
            const files = Array.from(e.target.files || [])
            e.target.value = ''
            if (locked) {
              modal.alert({ tone: 'warn', title: 'Thiệp đã khoá', message: lockNote(lockState) })
              return
            }
            startCropQueue(files, GALLERY_RATIO, 'gallery')
          }}
        />
      )}
    </div>
  )
}

function lockNote(st) {
  if (!st) return 'Thiệp đang bị khoá sửa.'
  if (st.reason === 'past_wedding')
    return `Thiệp đã khoá sửa (quá ${st.lockDaysAfterWedding} ngày sau ngày cưới). Liên hệ admin nếu cần mở lại.`
  if (st.reason === 'out_of_edits')
    return `Đã dùng hết ${st.maxEdits} lượt sửa. Liên hệ admin nếu cần cấp thêm lượt.`
  return 'Thiệp đang bị khoá sửa.'
}

const S = {
  wrap: {
    display: 'flex',
    flexDirection: 'column',
    height: '100vh',
    width: '100%',
    backgroundColor: '#1f1917',
    fontFamily: "'Plus Jakarta Sans', sans-serif",
  },
  topbar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 14,
    padding: '10px 18px',
    background: 'rgba(31, 25, 23, 0.96)',
    backdropFilter: 'blur(12px)',
    borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
    color: '#fff',
    flexShrink: 0,
    flexWrap: 'wrap',
    zIndex: 20,
  },
  left: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    flex: 1,
    minWidth: 220,
  },
  backBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 5,
    background: 'rgba(255, 255, 255, 0.08)',
    border: '1px solid rgba(255, 255, 255, 0.18)',
    color: '#fff',
    borderRadius: 9999,
    padding: '6px 14px',
    cursor: 'pointer',
    fontSize: 12.5,
    fontWeight: 500,
    whiteSpace: 'nowrap',
    transition: 'all 0.2s ease',
  },
  titleWrap: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    overflow: 'hidden',
  },
  title: {
    fontSize: 14,
    fontWeight: 600,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  centerStatus: {
    display: 'flex',
    alignItems: 'center',
  },
  statusPill: {
    fontSize: 12.5,
    fontWeight: 600,
    padding: '6px 14px',
    borderRadius: 9999,
    whiteSpace: 'nowrap',
    border: '1px solid rgba(255,255,255,0.08)',
  },
  lockBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    fontSize: 12.5,
    fontWeight: 600,
    background: 'rgba(201, 169, 110, 0.25)',
    color: '#e5c378',
    padding: '6px 14px',
    borderRadius: 9999,
    whiteSpace: 'nowrap',
  },
  rightActions: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
  },
  tplBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    background: 'rgba(255, 255, 255, 0.08)',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    color: '#fff',
    borderRadius: 9999,
    padding: '7px 15px',
    cursor: 'pointer',
    fontSize: 12.5,
    fontWeight: 500,
    whiteSpace: 'nowrap',
    transition: 'all 0.2s ease',
  },
  saveBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    background: 'linear-gradient(135deg, #e58d6f 0%, #c96547 100%)',
    border: 'none',
    color: '#fff',
    borderRadius: 9999,
    padding: '8px 18px',
    cursor: 'pointer',
    fontSize: 13,
    fontWeight: 600,
    whiteSpace: 'nowrap',
    boxShadow: '0 4px 14px rgba(217, 119, 87, 0.35)',
    transition: 'all 0.2s ease',
  },
  viewLink: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 5,
    color: '#fff',
    fontSize: 12.5,
    fontWeight: 500,
    textDecoration: 'none',
    whiteSpace: 'nowrap',
    background: 'rgba(255, 255, 255, 0.08)',
    padding: '7px 14px',
    borderRadius: 9999,
    border: '1px solid rgba(255, 255, 255, 0.18)',
  },
  lockBar: {
    background: 'rgba(201, 169, 110, 0.15)',
    color: '#f4ecdc',
    fontSize: 12.5,
    padding: '8px 18px',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    borderBottom: '1px solid rgba(201, 169, 110, 0.25)',
  },
  iframe: {
    flex: 1,
    width: '100%',
    border: 'none',
    background: '#fff',
  },
  center: {
    width: '100%',
    height: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#fcfaf7',
    fontFamily: "'Plus Jakarta Sans', sans-serif",
    padding: 20,
  },
  loadCard: {
    background: '#fff',
    border: '1px solid #ede5db',
    borderRadius: 20,
    padding: '36px 32px',
    textAlign: 'center',
    boxShadow: '0 10px 30px rgba(31, 25, 23, 0.08)',
    maxWidth: 420,
  },
  btnBackHome: {
    padding: '10px 22px',
    background: '#d97757',
    color: '#fff',
    border: 'none',
    borderRadius: 9999,
    cursor: 'pointer',
    fontSize: 13,
    fontWeight: 600,
  },
  bankOverlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(31,25,23,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 20,
    zIndex: 50,
  },
  bankSheet: {
    width: 'min(560px, 100%)',
    maxHeight: '90vh',
    overflowY: 'auto',
    background: '#fcfaf7',
    borderRadius: 20,
    boxShadow: '0 24px 60px rgba(0,0,0,0.3)',
  },
  bankHead: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 12,
    padding: '20px 22px 14px',
    borderBottom: '1px solid #ede5db',
  },
  bankTitle: { fontSize: 16, fontWeight: 700, color: '#1f1917', display: 'flex', alignItems: 'center', gap: 8 },
  bankSub: { fontSize: 12.5, color: '#6e625c', marginTop: 6, lineHeight: 1.5 },
  bankClose: {
    background: '#f6f1eb',
    border: '1px solid #ede5db',
    borderRadius: 10,
    padding: 8,
    cursor: 'pointer',
    color: '#6e625c',
    display: 'flex',
    flexShrink: 0,
  },
  bankBody: { padding: '8px 18px 20px' },
}
